"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Check, ChevronDown, Language } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useI18n, type Locale } from "@/lib/i18n/I18nProvider";
import { popIn } from "@/lib/motion";
import { cn } from "@/lib/utils";

// Cada idioma se nombra en su propia lengua, sin banderas: un idioma no es un país.
const OPTIONS: { locale: Locale; code: string; labelKey: "login.languageSpanish" | "login.languageEnglish" }[] = [
  { locale: "es", code: "ES", labelKey: "login.languageSpanish" },
  { locale: "en", code: "EN", labelKey: "login.languageEnglish" },
];

export function LanguageSelector() {
  const { t, locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = OPTIONS.find((o) => o.locale === locale) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    function handlePointer(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t("login.languageLabel")}: ${t(current.labelKey)}`}
        className="h-9 gap-1.5 rounded-pill px-3 text-[13px] font-medium text-muted-foreground shadow-none transition-colors duration-200 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.08] focus-visible:ring-4 focus-visible:ring-ring/30 focus-visible:ring-offset-0"
      >
        <Language className="h-4 w-4" />
        <span>{current.code}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")} />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.ul
            {...popIn}
            role="list"
            className="glass-strong absolute right-0 top-full z-50 mt-2 flex min-w-[160px] origin-top-right flex-col gap-0.5 rounded-card p-1.5"
          >
            {OPTIONS.map((option) => {
              const selected = locale === option.locale;
              return (
                <li key={option.locale}>
                  <button
                    type="button"
                    lang={option.locale}
                    aria-current={selected ? "true" : undefined}
                    onClick={() => {
                      setLocale(option.locale);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className={cn(
                      "flex h-9 w-full items-center gap-2.5 rounded-[14px] px-3 text-left text-sm transition-colors duration-200",
                      "hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30 dark:hover:bg-white/[0.08]",
                      selected ? "font-semibold text-foreground" : "text-muted-foreground"
                    )}
                  >
                    <span className="flex-1">{t(option.labelKey)}</span>
                    {selected && <Check className="h-4 w-4 text-brand-primary" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
